import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, CheckCircle2, AlertCircle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, LoadingBlock } from "@/components/common/States";
import { AppList, AppRow } from "@/components/common/Motion";
import api from "@/lib/api";

const ICON = {
  publish_success: CheckCircle2, publish_failed: AlertCircle,
  approval_submitted: Bell, approval_approved: CheckCircle2, changes_requested: AlertCircle,
  connection_expiring: AlertCircle, plan_limit: AlertCircle,
};

export default function Notifications() {
  const [items, setItems] = useState(null);
  // The stagger runs once, on the first render that has rows. Marking one as
  // read refetches the list, and replaying the entrance every time would be
  // maddening. A ref rather than state, because flipping state in the same
  // batch as the rows would mark the list settled before it ever animated.
  const hasRendered = useRef(false);
  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data.items); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (items) hasRendered.current = true; }, [items]);

  async function readAll() {
    await api.post("/notifications/read-all");
    toast.success("Marked all as read");
    load();
  }

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle="Approvals, publishes, and everything you should know."
        actions={
          <button data-testid="mark-read-all" onClick={readAll} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">
            Mark all as read
          </button>
        }
      />
      <div className="rounded-xl border border-[#E4E9EF] bg-white">
        {items === null ? <div className="p-6"><LoadingBlock rows={4} /></div> :
          items.length === 0 ? <div className="p-8"><EmptyState icon={Bell} title="No notifications yet" body="Once your team gets moving, updates will show up here." /></div> :
            <AppList as="ul" settled={hasRendered.current} className="divide-y divide-[#E4E9EF]">
              {items.map(n => {
                const Icon = ICON[n.kind] || Bell;
                return (
                  <AppRow as="li" key={n.id} data-testid={`notif-${n.id}`} className={`flex items-start gap-3 p-4 ${n.read ? "" : "bg-[#F6F8FA]/40"}`}>
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEF3FB] text-[#1F5FD8]"><Icon className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#0B2B33]">{n.title}</div>
                      {n.body ? <div className="text-xs text-[#43535E]">{n.body}</div> : null}
                      <div className="mt-1 text-[11px] text-[#8A97A0]">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                    {!n.read ? <span className="mt-1 h-2 w-2 rounded-full bg-[#1F5FD8]" /> : null}
                  </AppRow>
                );
              })}
            </AppList>
        }
      </div>
    </div>
  );
}
